import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import Box from "ds/components/Box";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import useTitle from "hooks/useTitle";
import useCanManagePolicy from "shared/Policy/access/useCanManagePolicy";
import useCanReadPolicy from "shared/Policy/access/useCanReadPolicy";
import { Policy, PolicyEngineType } from "types/generated";

import PolicySimulation from "../../PolicySimulation";
import PolicyDetailsBodyContent from "./Content";
import styles from "./styles.module.css";

type PolicyDetailsBodyProps = {
  policy: Policy;
  initialBody: string;
};

const PolicyDetailsBody = ({ policy, initialBody }: PolicyDetailsBodyProps) => {
  const [body, setBody] = useState(initialBody);
  const [policyEngineType, setPolicyEngineType] = useState<PolicyEngineType>(policy.engineType);
  const [searchParams] = useSearchParams();
  const canManagePolicy = useCanManagePolicy()(policy);
  const canSimulatePolicies = useCanReadPolicy()(policy);
  const isBodyChanged = initialBody !== body;

  useBlockNavigationModal({
    isNavigationBlocked: isBodyChanged,
    onConfirm: () => {
      setBody(initialBody);
    },
  });

  useTitle(`Policy body · ${policy.name}`);

  const handleEditorChange = (value?: string) => {
    setBody(value || "");
  };

  const evaluationKey = searchParams.get("evaluationKey");

  const [isSimulationVisible, setIsSimulationVisible] = useState(false);

  useEffect(() => {
    if (canManagePolicy.result && evaluationKey) {
      setIsSimulationVisible(true);
    }
  }, [evaluationKey, canManagePolicy]);

  return (
    <Box justify="stretch" grow="1">
      <PolicyDetailsBodyContent
        body={body}
        handleEditorChange={handleEditorChange}
        policy={policy}
        policyEngineType={policyEngineType}
        setPolicyEngineType={setPolicyEngineType}
        isSimulationVisible={isSimulationVisible}
        canManagePolicy={canManagePolicy}
        canSimulatePolicies={canSimulatePolicies}
      />

      {canSimulatePolicies.result && (
        <PolicySimulation
          className={styles.simulationWrapper}
          policyId={policy.id}
          body={body}
          type={policy.type}
          engineType={policyEngineType}
          isSimulationVisible={isSimulationVisible}
          setIsSimulationVisible={setIsSimulationVisible}
          evaluationKey={evaluationKey || undefined}
        />
      )}
    </Box>
  );
};

export default PolicyDetailsBody;
